/**
 * 2024-07-10 changed logic to use max_adults instead of max_occupancy
 * @param {*} unitCategories 
 * @param {*} totalGuests 
 * @returns 
 */
function findBestRoomCombination(unitCategories, totalGuests) {
  const unitCategoriesCount = unitCategories.length;
  const dp = Array.from({ length: unitCategoriesCount + 1 }, () => new Array(totalGuests + 1).fill(Infinity));
  dp[0][0] = 0;

  for (let i = 1; i <= unitCategoriesCount; i++) {
    const unitCategory = unitCategories[i - 1];
    const unitCategoryPrice = unitCategory.room_types.reduce((acc, rt) => acc + parseFloat(rt.amount), 0) / unitCategory.room_types.length;

    for (let j = 0; j <= totalGuests; j++) {
      // Always include the previous value
      dp[i][j] = dp[i - 1][j];

      // Check if the unit category can be used for this number of guests
      if (j >= unitCategory.max_adults) {
        dp[i][j] = Math.min(dp[i][j], dp[i - 1][j - unitCategory.max_adults] + unitCategoryPrice);
      } else if (j <= unitCategory.max_adults) {
        // Consider the unit category even if it doesn't fully match the remaining guests
        dp[i][j] = Math.min(dp[i][j], dp[i - 1][0] + unitCategoryPrice);
      }
    }
  }

  // Backtrack to find the combination of unit categories
  let combination = [];
  let remainingGuests = totalGuests;
  for (let i = unitCategoriesCount; i > 0 && remainingGuests > 0; i--) {
    if (dp[i][remainingGuests] != dp[i - 1][remainingGuests]) {
      combination.push({
        ...unitCategories[i - 1],
        guestsAllocated: unitCategories[i - 1].max_adults < remainingGuests ? unitCategories[i - 1].max_adults : remainingGuests,
      });
      remainingGuests -= unitCategories[i - 1].max_adults;
    }
  }

  for (let i = unitCategoriesCount; i > 0 && remainingGuests > 0; i--) {
    if (remainingGuests >= unitCategories[i - 1].max_adults && dp[i][remainingGuests] === dp[i - 1][remainingGuests - unitCategories[i - 1].max_adults] + unitCategories[i - 1].price) {
      combination.push({
        ...unitCategories[i - 1],
        guestsAllocated: unitCategories[i - 1].max_adults < remainingGuests ? unitCategories[i - 1].max_adults : remainingGuests,
      });
      remainingGuests -= unitCategories[i - 1].max_adults;
    } else if (remainingGuests < unitCategories[i - 1].max_adults && dp[i][remainingGuests] === dp[i - 1][0] + unitCategories[i - 1].price) {
      combination.push({
        ...unitCategories[i - 1],
        guestsAllocated: unitCategories[i - 1].max_adults < remainingGuests ? unitCategories[i - 1].max_adults : remainingGuests,
      });
      remainingGuests = 0;
    }
  }

  return combination;
}

import { minBy, sumBy } from 'lodash';

export default {
  methods: {
    findCombinations() {
      // usciamo dalla funzione se sono disponibili singole unità in base ai criteri di ricerca (findCombinations)
      if (this.filteredUnitCategories.length) {
        return;
      }
      // Calcolo del numero totale di ospiti
      const guests = this.searchFilters.adults + this.searchFilters.children;

      // Espansione delle unitCategories in base alla loro disponibilità
      let expandedUnitCategories = [];
      this.unitCategories.forEach(unitCategory => {
        for (let i = 0; i < unitCategory.availability; i++) {
          expandedUnitCategories.push({...unitCategory, availableUnits: 1});
        }
      });

      let bestCombination = findBestRoomCombination(expandedUnitCategories, guests);

      // Aggregazione delle unità con lo stesso id e calcolo di guestsAllocated e guestRateNeeded
      let subtractiveCombination = {};
      if (bestCombination) {
        bestCombination.forEach(item => {
          let key = `${item.id}-${item.guestsAllocated}`;
          if (!subtractiveCombination[key]) {
            subtractiveCombination[key] = { ...item, necessaryUnits: 0, guestsAllocated: 0 };
          }
          subtractiveCombination[key].necessaryUnits += 1;
          subtractiveCombination[key].guestsAllocated += item.guestsAllocated;
        });

        // Calcolo di guestRateNeeded e pulizia di room_types
        for (const key in subtractiveCombination) {
          let unit = subtractiveCombination[key];
          unit.guestRateNeeded = Math.ceil(unit.guestsAllocated / unit.necessaryUnits);

          // Controlla se c'è un room_type che corrisponde esattamente al numero di ospiti necessari
          const exactMatchRoomTypes = unit.room_types.filter(rt => rt.guests === unit.guestRateNeeded);

          if (exactMatchRoomTypes.length > 0) {
            // Se esiste, usa solo questi room_types
            unit.room_types = exactMatchRoomTypes;
          } else {
            // Altrimenti, trova tutti i room_types con il numero di ospiti più vicino e inferiore a guestRateNeeded
            const closestGuestCount = Math.max(...unit.room_types.filter(rt => rt.guests < unit.guestRateNeeded).map(rt => rt.guests));
            const closestRoomTypes = unit.room_types.filter(rt => rt.guests === closestGuestCount);

            if (closestRoomTypes.length > 0) {
              unit.room_types = closestRoomTypes;
            } else {
              // Se non ci sono room_types con numero inferiore di ospiti, prendi il più grande disponibile
              const maxRoomType = unit.room_types.reduce((max, rt) => (rt.guests > max.guests ? rt : max), unit.room_types[0]);
              unit.room_types = [maxRoomType];
            }
          }

          // Trova il miglior piano tariffario tra i room_types corrispondenti
          const bestRoomType = this.findBestRoomType(unit.room_types);
          if (bestRoomType) {
            unit.selectedRatePlanId = bestRoomType.rate_plan_id; // ID del piano tariffario selezionato
            unit.selectedRoomTypeDetails = bestRoomType; // Dettagli completi del piano tariffario selezionato
          }
        }

        // Trasformazione in array
        subtractiveCombination = Object.values(subtractiveCombination);
      } else {
        // Nessuna combinazione trovata
        subtractiveCombination = [];
      }

      this.unitCombination = subtractiveCombination;
      this.loadingUnitCombinations = false;
    },
    findBestRoomType(room_types) {
      if (!room_types || !room_types.length) {
        return null;
      }

      const bestPlan = minBy(room_types, (rt) => {
        return sumBy(rt.rates, rate => parseFloat(rate.amount));
      });

      return bestPlan ? { ...bestPlan, room_type_total: sumBy(bestPlan.rates, rate => parseFloat(rate.amount)) } : null;
    },
    updateSelectedRoomTypeDetails(unitCategory, selectedRatePlanId) {
      const selectedRoomType = unitCategory.room_types.find(rt => rt.rate_plan_id === selectedRatePlanId);
      if (selectedRoomType) {
        unitCategory.selectedRoomTypeDetails = selectedRoomType;
      }
    },
    getRoomTypePromotionsDetails(roomType) {
      let activePromotion = {};

      if (this.hasRoomTypePromotion(roomType)) {
        let promotions = this.getRoomTypePromotions(roomType, this.property);

        if (promotions.length) {
          activePromotion = promotions[0];
        }
      }

      return activePromotion;
    },
  },
  computed: {
    totalBookingCost() {
      if (!this.unitCombination) return 0;

      return this.unitCombination.reduce((total, unitCategory) => {
        let roomTypeTotal;
        if (this.hasRoomTypePromotion(unitCategory.selectedRoomTypeDetails)) {
          // Calcolo del prezzo con promozioni
          roomTypeTotal = this.calculateTotalPrice(unitCategory.selectedRoomTypeDetails.rates, { 
            roomType: unitCategory.selectedRoomTypeDetails, 
            withRoomTypePromotions: true
          });
        } else {
          // Calcolo del prezzo senza promozioni
          roomTypeTotal = this.calculateTotalPrice(unitCategory.selectedRoomTypeDetails.rates, { 
            roomType: unitCategory.selectedRoomTypeDetails, 
            withRoomTypePromotions: false
          });
        }

        const cleaningFeeTotal = unitCategory.cleaning_fee ? unitCategory.cleaning_fee * unitCategory.necessaryUnits : 0;

        // Moltiplica il totale della camera per il numero di unità necessarie e aggiungi la tariffa di pulizia
        return total + (roomTypeTotal * unitCategory.necessaryUnits) + cleaningFeeTotal;
      }, 0);
    },
    filteredUnitCategories() {
      // const totalGuests = this.searchFilters.adults + this.searchFilters.children;
      // return this.unitCategories.filter(uc => uc.max_occupation >= totalGuests);

      return this.unitCategories.filter(uc => uc.max_adults >= this.searchFilters.adults && uc.max_children >= this.searchFilters.children);
    },
  }
}
